<template>
  <Multiselect
    ref="multiselect"
    v-bind="$attrs"
    class="export-select"
    :class="{ 'export-select--small': size === 'sm' }"
    :value="selectedOption"
    :options="normalizedOptions"
    track-by="key"
    label="label"
    :allow-empty="allowEmpty"
    :close-on-select="true"
    :disabled="disabled"
    :max-height="maxHeight"
    :placeholder="placeholder"
    :searchable="searchable"
    :show-labels="false"
    @input="handleInput"
  >
    <template #option="{ option }">
      <div class="export-select__option">
        <span
          class="export-select__check"
          :class="{ 'export-select__check--visible': isSelected(option) }"
          aria-hidden="true"
        />
        <span>{{ option.label }}</span>
      </div>
    </template>
    <template #noResult>
      <span class="export-select__empty">No options found.</span>
    </template>
  </Multiselect>
</template>

<script>
/* eslint vue/enforce-style-attribute: 0 */
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';

export default {
  name: 'ExportSelect',
  components: { Multiselect },
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number, Boolean, Object, Array],
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Select option'
    },
    searchable: {
      type: Boolean,
      default: false
    },
    allowEmpty: {
      type: Boolean,
      default: false
    },
    maxHeight: {
      type: Number,
      default: 280
    },
    size: {
      type: String,
      default: null
    },
    emitOption: {
      type: Boolean,
      default: false
    },
    optionLabel: {
      type: String,
      default: null
    }
  },
  computed: {
    normalizedOptions() {
      return this.options.map((option, index) => {
        const isObjectOption = option !== null && typeof option === 'object' && !Array.isArray(option);
        const value = isObjectOption && Object.prototype.hasOwnProperty.call(option, 'value')
          ? option.value
          : option;
        const label = isObjectOption
          ? (this.optionLabel ? option[this.optionLabel] : null) ?? option.text ?? option.label ?? String(value ?? '')
          : String(option ?? '');

        return {
          key: `${index}-${String(label)}`,
          label,
          value,
          raw: option,
          $isDisabled: Boolean(isObjectOption && (option.disabled || option.$isDisabled))
        };
      });
    },
    selectedOption() {
      return this.normalizedOptions.find(option => this.isSelected(option)) ?? null;
    }
  },
  methods: {
    isSelected(option) {
      return (this.emitOption ? option.raw : option.value) === this.value;
    },
    handleInput(option) {
      const value = option
        ? (this.emitOption ? option.raw : option.value)
        : null;
      this.$emit('input', value);
      this.$emit('change', value);
      this.$emit('select', value);
    }
  }
};
</script>

<style lang="scss">
.export-select.multiselect {
  position: relative;
  min-height: 40px;
  color: #172033;
  font-size: 14px;

  &.form-control {
    height: auto;
    padding: 0;
    border: 0;
    background: transparent;
  }

  .multiselect__tags {
    min-height: 40px;
    padding: 9px 38px 7px 12px;
    border: 1px solid #dfe5ed;
    border-radius: 4px;
    background: #fff;
    box-shadow: none;
  }

  .multiselect__single,
  .multiselect__placeholder {
    margin: 0;
    padding: 0;
    color: #172033;
    line-height: 20px;
  }

  .multiselect__placeholder {
    color: #64748b;
  }

  .multiselect__select {
    width: 38px;
    height: 38px;

    &::before {
      top: 60%;
      border-width: 5px 4px 0;
      border-top-color: #64748b;
    }
  }

  .multiselect__content-wrapper {
    z-index: 2001;
    margin-top: 3px;
    border: 1px solid #dfe5ed;
    border-radius: 4px;
    background: #fff;
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.14);
  }

  .multiselect__option {
    min-height: 32px;
    padding: 7px 12px;
    color: #172033;
    background: #fff;
    line-height: 18px;

    &::after {
      display: none;
    }

    &--selected {
      color: #172033;
      background: #fff;
      font-weight: 400;
    }

    &--highlight,
    &--selected.multiselect__option--highlight {
      color: #fff;
      background: #19a17f;
    }

    &--disabled {
      color: #94a3b8;
      background: #f8fafc;
    }
  }

  .export-select__option {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .export-select__check {
    position: relative;
    flex: 0 0 12px;
    width: 12px;
    height: 12px;
    opacity: 0;

    &--visible {
      opacity: 1;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 3px;
        width: 5px;
        height: 9px;
        border: solid currentColor;
        border-width: 0 1.5px 1.5px 0;
        transform: rotate(45deg);
      }
    }
  }

  &.multiselect--active .multiselect__tags,
  &:focus-within .multiselect__tags {
    border-color: #209c86;
    box-shadow: 0 0 0 2px rgba(32, 156, 134, 0.12);
  }

  &.multiselect--active {
    z-index: 2000;
  }

  &.multiselect--disabled {
    opacity: 0.6;

    .multiselect__tags {
      background: #f8fafc;
    }
  }

  &.export-select--small {
    min-height: 32px;

    .multiselect__tags {
      min-height: 32px;
      padding-top: 5px;
      padding-bottom: 4px;
    }

    .multiselect__select {
      height: 30px;
    }
  }
}
</style>
